'use client';

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ComponentContainer } from '@/components/atoms/ComponentContainer/ComponentContainer';
import { Image } from '@/components/atoms/Image/Image';
import { ICommonProps, TProject, TProjectVisibility } from '@/types/global';
import { usePageStore } from '@/store/usePageStore';
import { emailEncoded } from '@/utils/constants';
import { Typography } from '@/components/atoms/Typography/Typography';
import { useTranslations } from 'next-intl';
import { CustomLink } from '@/components/atoms/CustomLink/CustomLink';
import { Video } from '@/components/atoms/Video/Video';
import * as Styled from './ArchivedProjectsGallery.styled';

export interface IArchivedProjectsGallery extends ICommonProps {
  projects: TProject[] | undefined;
  isAdditionalList?: boolean;
  isImagesWithProjects?: boolean;
}

function chunkArray<T>(array: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}

const ArchivedProjectsGalleryComponentFn: React.FC<IArchivedProjectsGallery> = ({
  projects,
  width = 'main',
  isProject = false,
  isAdditionalList = false,
  isImagesWithProjects = false,
}) => {
  const t = useTranslations('work');
  const content = t.raw('archivedProjects') as { title: string };

  const contentPrivate = t.raw('filteredProjectsGallery') as {
    titlePrivateProject: string;
    textPrivateProject: string;
  };

  const [pagesToRender, setPagesToRender] = useState(1);
  const pageSize = 12;

  const projectPages = useMemo(() => chunkArray(projects || [], pageSize), [projects]);
  const totalPages = projectPages.length;
  const lastItemRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const observeLastElement = useCallback((node: HTMLDivElement | null) => {
    // avoids setting observer on the same node multiple times
    if (lastItemRef.current === node) return; 
    if (observerRef.current) observerRef.current.disconnect();

    if (node) {
      observerRef.current = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          setPagesToRender((prev) => {
            if (prev >= totalPages) return prev;
            return prev + 1;
          });
        }
      }, {
        threshold: 0.9,
        rootMargin: '200px',
      });
      observerRef.current.observe(node);
    }

    lastItemRef.current = node;
  }, [totalPages]);

  const isUserLogged = usePageStore.getState().isUserLogged;

  const isBlockedByPrivacy = (visibility: TProjectVisibility = 'open') => {
    return (visibility === 'private' && !isUserLogged) && !isUserLogged;
  };

  return projects && projects.length > 0 && (
    <ComponentContainer width={width} marginBottom={'none'} isProject={isProject}>
      <Styled.ArchiveGalleryContainer data-is-additional-list={isAdditionalList}>
        {!isAdditionalList && <Typography text={content.title} size="lg" />}

        {projects && projects.length > 0 && (
          projectPages.slice(0, pagesToRender).map((page, pageIndex) => (
            <div
              key={`archive-page-${pageIndex}`}
              ref={pageIndex === pagesToRender - 1 ? observeLastElement : null}
              style={{ marginBottom: '4rem' }}
              className="archived-batch-container"
            >
              {page.map((project, i) => (
                <CustomLink
                  key={`project-archived-${pageIndex}-${i}`}
                  href={`${project.slug}`}
                  className="archived-project-link"
                  isDisabled={isImagesWithProjects ? isImagesWithProjects : isBlockedByPrivacy(project.visibility)}
                >
                  {project?.videoThumbnail ? (
                    <Video
                      {...{
                        ...project.videoThumbnail,
                        vimeoMetadata:
                          project.videoThumbnail.vimeoMetadata ?? undefined,
                      }}
                      includeContainer={true}
                      fill={true}
                      aspectRatio="3/2"
                    />
                  ) : (
                    <Image
                      {...project.image}
                      src={project.image?.src || ''}
                      aspectRatio={'3/2'}
                      includeContainer={true}
                      alt={project.title}
                      includeInSlider={isImagesWithProjects}
                    />
                  )}
                  {isBlockedByPrivacy(project.visibility) && (
                    <Styled.TextExclusiveContent
                      data-is-logged={isUserLogged}
                    >
                      <Typography
                        text={contentPrivate.titlePrivateProject}
                        color="white"
                        size="base"
                      />
                      <Typography
                        text={{
                          en: `Contact us at ${emailEncoded}`,
                          es: `Contáctenos en ${emailEncoded}`
                        }}
                        color="white"
                        size="sm"
                      />
                    </Styled.TextExclusiveContent>
                  )}
                  <CustomLink href={project.slug} className="text-container" isDisabled={!isImagesWithProjects}>
                    <Typography text={project.title} size="sm" />
                    <Typography text={project.year} size="sm" color="secondary" />
                  </CustomLink>
                </CustomLink>
              ))}
            </div>
          ))
        )}
      </Styled.ArchiveGalleryContainer>
    </ComponentContainer>
  );
};

// we need to memoize the component to avoid video re-rendering on every view enter
export const ArchivedProjectsGallery = React.memo(ArchivedProjectsGalleryComponentFn);
ArchivedProjectsGallery.displayName = 'ArchivedProjectsGallery';